import { Label } from '@/components/ui/label';
import { HelpCircle, User, Plus , Loader2 , AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectContentHighPriority,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';
import type { ObrigatoriedadeConfig } from '@/hooks/useObrigatoriedadesItem';
import { GestorCessionarioEditDialog } from '@/components/carteira/GestorCessionarioEditDialog';

interface Props {
  config: ObrigatoriedadeConfig;
  value: string | null;
  onChange: (value: string) => void;
  clienteId: string;
  erro?: string;
  obrigatorio?: boolean;
}

export function GestorField({ config, value, onChange, clienteId, erro, obrigatorio = false }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [mostrarFormNovo, setMostrarFormNovo] = useState(false);
  const [criando, setCriando] = useState(false);

  // Se clienteId não foi fornecido, não renderizar
  if (!clienteId || clienteId.trim() === '') {
    console.error('❌ GestorField: clienteId inválido!', { clienteId });
    return (
      <div className="space-y-2">
        <Label className="text-destructive">Gestor no Cliente</Label>
        <p className="text-xs text-destructive">
           Aguardando identificação do cliente...
        </p>
      </div>
    );
  }

  // Adicionar log ANTES da query
  console.log('🔄 GestorField: Iniciando query com clienteId:', clienteId);

  const { data: gestores, isLoading, isError, error } = useQuery({
    queryKey: ['cliente-contatos', clienteId],
    queryFn: async () => {
      console.log('🔄 Buscando gestores para cliente:', clienteId);
      const { data, error } = await supabase
        .from('cliente_contato')
        .select('id, nome, telefone')
        .eq('cliente_id', clienteId)
        .order('nome');
      
      if (error) {
        console.error('❌ Erro ao buscar gestores:', error);
        throw error;
      }
      
      console.log('✅ Gestores encontrados:', data);
      return data || [];
    },
    enabled: !!clienteId,
  });

  console.log('📋 Gestores carregados:', {
    clienteId,
    isLoading,
    total: gestores?.length || 0,
    gestores: gestores?.map(g => g.nome)
  });

  const isValid = !erro && !!value;

  const handleCriarNovo = async (dados: any) => {
    setCriando(true);
    try {
      // REGRA MÁXIMA: Buscar gestor principal (ordem = 0) e ATUALIZAR se existe
      const { data: gestorPrincipal } = await supabase
        .from('cliente_contato')
        .select('id')
        .eq('cliente_id', clienteId)
        .eq('ordem', 0)
        .maybeSingle();
      
      const dadosGestor = {
        cliente_id: clienteId,
        nome: dados.nome,
        telefone: dados.telefone || null,
        whatsapp: dados.whatsapp || null,
        email: dados.email || null,
        cpf: dados.cpf || null,
        rg: dados.rg || null,
        ordem: 0,
        is_principal: true,
      };
      
      let gestorId: string;
      
      if (gestorPrincipal) {
        // Atualizar gestor existente
        const { error } = await supabase
          .from('cliente_contato')
          .update(dadosGestor)
          .eq('id', gestorPrincipal.id);
        
        if (error) throw error;
        gestorId = gestorPrincipal.id;
        
        toast({
          title: 'Gestor atualizado!',
          description: 'O gestor principal foi atualizado com sucesso.',
        });
      } else {
        // Criar novo gestor
        const { data: novoGestor, error } = await supabase
          .from('cliente_contato')
          .insert([dadosGestor])
          .select('id')
          .single();
        
        if (error) throw error;
        gestorId = novoGestor.id;
        
        toast({
          title: 'Gestor criado!',
          description: 'O novo gestor foi cadastrado com sucesso.',
        });
      }
      
      await queryClient.invalidateQueries({
        queryKey: ['cliente-contatos', clienteId]
      });
      
      onChange(gestorId);
      setMostrarFormNovo(false);
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao salvar gestor',
        description: error.message,
      });
    } finally {
      setCriando(false);
    }
  };

  const isDisabled = isLoading || isError;

  console.log('✅ GestorField VAI RENDERIZAR:', {
    clienteId,
    gestoresDisponiveis: gestores?.length || 0,
    valorSelecionado: value,
    obrigatorio,
    isDisabled,
    isLoading,
    isError,
    errorMessage: error?.message,
    gestoresUndefined: gestores === undefined,
    gestoresNull: gestores === null,
    gestoresVazio: Array.isArray(gestores) && gestores.length === 0
  });

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <User className="w-4 h-4 text-muted-foreground" />
        <Label className="font-medium">
          {config.obrigatoriedades_catalogo.nome} {obrigatorio && '*'}
        </Label>
        {config.mensagem_ajuda && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger type="button">
                <HelpCircle className="h-3 w-3 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-xs">{config.mensagem_ajuda}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {isValid && obrigatorio && (
          <span className="text-xs text-success">✓</span>
        )}
      </div>

      {isLoading && (
        <p className="text-xs text-muted-foreground"><Loader2 className="mr-1.5 inline h-3 w-3 animate-spin" />Carregando gestores…</p>
      )}

      {isError && (
        <p className="text-xs text-destructive"> Erro ao carregar gestores. Tente novamente.</p>
      )}

      <Select 
        value={value || undefined} 
        onValueChange={(newValue) => {
          console.log('🎯 [GestorField] SELECT CHANGED:', {
            valorAntigo: value,
            valorNovo: newValue,
            clienteId,
            timestamp: new Date().toISOString()
          });
          
          // Verificar se o valor é válido
          if (!newValue || newValue.trim() === '') {
            console.error('❌ [GestorField] Valor inválido selecionado!');
            return;
          }
          
          console.log('✅ [GestorField] Chamando onChange com valor:', newValue);
          onChange(newValue);
        }}
        disabled={isDisabled}
        onOpenChange={(isOpen) => {
          console.log('🔓 [GestorField] DROPDOWN ABRIU/FECHOU:', {
            isOpen,
            isDisabled,
            gestoresDisponiveis: gestores?.length || 0,
            clienteId,
            timestamp: new Date().toISOString()
          });
        }}
      >
        <SelectTrigger 
          className={erro ? 'border-destructive' : isValid ? 'border-success' : ''}
          disabled={isDisabled}
          onClick={(e) => {
            console.log('🖱️ GestorField TRIGGER CLICADO:', {
              isDisabled,
              gestoresLength: gestores?.length || 0,
              clienteId,
              propagationStopped: e.isPropagationStopped(),
              defaultPrevented: e.defaultPrevented,
              timestamp: new Date().toISOString()
            });
          }}
        >
          <SelectValue placeholder={
            isLoading 
              ? "Carregando gestores…" 
              : isError 
              ? "Erro ao carregar" 
              : gestores && gestores.length === 0
              ? "Nenhum gestor cadastrado"
              : "Selecione o gestor..."
          } />
        </SelectTrigger>
        <SelectContentHighPriority 
          position="popper" 
          sideOffset={5}
        >
          <div className="p-2 border-b">
            <Button 
              size="sm" 
              variant="outline"
              className="w-full justify-start gap-2"
              onClick={(e) => {
                e.preventDefault();
                setMostrarFormNovo(true);
              }}
            >
              <Plus className="h-4 w-4" />
              + Novo Gestor
            </Button>
          </div>
          {gestores && gestores.length > 0 ? (
            gestores.map((gestor) => (
              <SelectItem 
                key={gestor.id} 
                value={gestor.id}
              >
                {gestor.nome}
              </SelectItem>
            ))
          ) : (
            <div className="p-3 text-center text-sm text-muted-foreground">
              <p><AlertTriangle className="mr-1.5 inline h-3 w-3" />Nenhum gestor cadastrado ainda</p>
            </div>
          )}
        </SelectContentHighPriority>
      </Select>

      <GestorCessionarioEditDialog
        open={mostrarFormNovo}
        onOpenChange={setMostrarFormNovo}
        tipo="gestor"
        dadosIniciais={null}
        onSave={handleCriarNovo}
      />

      {!obrigatorio && !value && (
        <p className="text-xs text-muted-foreground">
           Opcional agora - será obrigatório ao fechar o pedido
        </p>
      )}

      {erro && obrigatorio && (
        <p className="text-xs text-destructive">
          {config.mensagem_erro || erro}
        </p>
      )}
    </div>
  );
}
